import { createContext, useContext, useEffect, useReducer, useRef } from 'react'

import { editUser, getUser } from '../apiRoutes/userRoutes'

import { useAuth } from './AuthenticationContext'
import { useSocket } from './SocketContext'
import { Grid } from '../components/UniversalComponents'
import { CircularProgress, Typography } from '@mui/material'
import { useToastMessage } from './ToastPopUpContext'
import { getAuthentication } from '../apiRoutes'

type Props = {
    children: React.ReactNode
}

export const GlobalState = createContext({
    globalState: {
        userPreferences: {
            boatManagementTable: [],
            dataTableColumns: {
                devices: {
                    columns: {},
                    density: '',
                },
                engines: {
                    engine: {
                        columns: {},
                        density: '',
                    },
                    engineCalibration: {
                        columns: {},
                        density: '',
                    },
                    engineModel: {
                        columns: {},
                        density: '',
                    },
                    engineManufacturers: {
                        columns: {},
                        density: '',
                    },
                },
                boats: {
                    boatConfig: {
                        columns: {},
                        density: '',
                    },
                    boatModels: {
                        columns: {},
                        density: '',
                    },
                    boatManufacturers: {
                        columns: {},
                        density: '',
                    },
                },
                dealers: {
                    columns: {},
                    density: '',
                },
                signals: {
                    signalManager: {
                        columns: {},
                        density: '',
                    },
                    signalEvents: {
                        columns: {},
                        density: '',
                    },
                    unitOfMeasurement: {
                        columns: {},
                        density: '',
                    },
                    signalProfileManager: {
                        columns: {},
                        density: '',
                    },
                    signalFormulaManager: {
                        columns: {},
                        density: '',
                    },
                },
                system: {
                    osPatchNotes: {
                        columns: {},
                        density: '',
                    },
                    mobilePatchNotes: {
                        columns: {},
                        density: '',
                    },
                },
                users: {
                    userAdmin: {
                        columns: {},
                        density: '',
                    },
                    userNotificationSubscription: {
                        columns: {},
                        density: '',
                    },
                },
                manufacturers: {
                    columns: {},
                    density: '',
                },
            },
        },
        oupUser: {
            activeManufacturer: { _id: '', manufacturerName: '' },
            _id: '',
            dealerProfile: '',
            userNotifications: [],
            userNotificationsArchived: [],
            userBoats: [],
            userProfile: {
                superAdmin: false,
                userProfilePermissions: {
                    Manufacturer: {
                        read: false,
                        write: false,
                    },
                    Users: {
                        read: false,
                        write: false,
                    },
                },
                dealerProfile: false,
                userProfileAssignedManufacturers: [],
            },
            assignedDealerProfile: {
                _id: '',
                dealers: [],
                manufacturers: [],
            },
            userEmail: '',
        },
        userProfile: {
            superAdmin: false,
            userProfilePermissions: {
                Users: {
                    read: false,
                    write: false,
                },
            },
            dealerProfile: false,
            userProfileAssignedManufacturers: [],
        },
    },

    setGlobalState: (values: any) => {},
    setUser: (param: any) => {},
})

export const GlobalStateContext = ({ children }: Props) => {
    const { auth, setAuth } = useAuth()
    const { authenticated, _id, loading: authLoading, signingIn } = auth

    const { apiSocket } = useSocket()

    const defaultState = {
        userPreferences: {},
        userRole: '',
        userPermissions: {},
        activeManufacturer: { manufacturerName: '' },
        manufacturer: [],
        _id: '',
        username: '',
        oupUser: {
            activeManufacturer: { _id: '', manufacturerName: '' },
            userProfile: {
                superAdmin: false,
                userProfilePermissions: {},
                userProfileAssignedManufacturers: [],
            },
            userEmail: '',
        },
        userProfile: { superAdmin: false },
        loading: true,
        update: false,
    }

    const reducer = (oldState: any, newState: any) => ({
        ...oldState,
        ...newState,
    })
    const [state, setState] = useReducer(reducer, defaultState)

    const { oupUser, loading, update } = state
    const setToastMessage = useToastMessage()

    const getData = async () => {
        try {
            const userData = await getUser({ _id: auth._id })
            if (userData && userData._id) {
                setState({ oupUser: userData, loading: false })
            } else {
                throw new Error('No user data found')
            }
        } catch (err: any) {
            console.error('Global state fetch error:', err.message)
            setState({ ...state, loading: false, error: err.message })
        }
    }

    const setGlobal = (newGlobalState) => {
        setState({ ...newGlobalState })
    }

    const getActiveManufacturerUpdate = async () => {
        try {
            const currentSession = await getAuthentication({
                _id,
            })

            if (currentSession) {
                setAuth({
                    ...currentSession,
                    authenticated: true,
                })
            } else {
                throw new Error('No user found')
            }
        } catch (error) {
            console.error('error')
        }
    }

    interface SetUserParams {
        key: string
        value: any
        type?: string
    }
    const setUser = async ({ key, value, type }: SetUserParams) => {
        try {
            let parsedFormData = {}

            if (!type) {
                parsedFormData = {
                    id: _id,
                    // ...state.oupUser,
                    [key]: value,
                }
            } else if (type === 'columnData') {
                parsedFormData = {
                    id: _id,
                    // ...state.oupUser,
                    userPreferences: {
                        ...state.oupUser.userPreferences,
                        dataTableColumns: {
                            ...state.oupUser.userPreferences?.dataTableColumns,
                            [key]: {
                                ...state.oupUser.userPreferences
                                    .dataTableColumns?.[key],
                                ...value,
                            },
                        },
                    },
                }
            }

            // console.log(parsedFormData, 'formData')
            let res: any

            res = await editUser(parsedFormData)
            if (res) {
                const { data, status } = res

                if (data && status === 200) {
                    getActiveManufacturerUpdate()
                    setState({ oupUser: oupUser, update: true })

                    setToastMessage(
                        `User: ${data.userEmail} preferences were successfully edited`,
                        'success'
                    )

                    // getData()
                } else {
                    throw new Error('User edit was unsuccessfull')
                }
            }
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err.message}`, 'error')
        }
    }

    useEffect(() => {
        if (auth._id && !auth.loading && !signingIn) {
            getData()
        } else if (!auth.loading && !authenticated) {
            setState({
                loading: false,
            })
        }
    }, [auth, auth._id])

    useEffect(() => {
        if (authenticated && _id) {
            apiSocket?.on('update-user', () => {
                getActiveManufacturerUpdate()
                // getData()
            })
        }
    }, [authenticated, _id])

    useEffect(() => {
        if (auth._id && !auth.loading && signingIn === true) {
            setState({ ...state, loading: true, error: null })
            getData()
        } else if (!auth.loading && !authenticated) {
            setState({
                loading: false,
            })
        }
    }, [signingIn])

    if (loading || authLoading) {
        return (
            <Grid item xs={12} md={12} lg={12}>
                <Typography align="center" sx={{ marginTop: 40 }}>
                    <CircularProgress />
                </Typography>
            </Grid>
        )
    }

    console.log('User Details:', oupUser)

    return (
        <GlobalState.Provider
            value={{
                globalState: state,
                setUser,
                setGlobalState: (values) => setState(values),
            }}
        >
            {children}
        </GlobalState.Provider>
    )
}

export const useGlobalState = () => useContext(GlobalState)
